/**
 * Business Central "Transactions to review" — untranslated enums, URL values and
 * limits (MR-18). Translated labels live in `useBcLabels()`.
 */
import type { BcScreenStatus, BcTable } from '@/types/bcNeedsAttention.types'

/**
 * `objectTypeId` of a GL transaction (a receipt). The API puts a row in the GL table on this
 * alone (`BusinessCentralResendService.TableFor`) — `bcDocumentType` is not enough: featuresdev
 * has GL receipts logged as `purchaseInvoices` whose document is a journal line.
 */
export const BC_GL_OBJECT_TYPE_ID = 50

/** `?table=` on the list page → the API's table name. */
export const BC_TABLE_PARAM = {
    payouts: 'Payouts',
    gl: 'GL',
} as const satisfies Record<string, BcTable>
export type BcTableParam = keyof typeof BC_TABLE_PARAM
export const BC_TABLE_PARAMS = Object.keys(BC_TABLE_PARAM) as BcTableParam[]

/** `?status=` on the list page → the API's `screenStatus`. */
export const BC_STATUS_PARAM = {
    toReview: 'ToReview',
    pending: 'Pending',
    resolved: 'Resolved',
} as const satisfies Record<string, BcScreenStatus>
export type BcStatusParam = keyof typeof BC_STATUS_PARAM
export const BC_STATUS_PARAMS = Object.keys(BC_STATUS_PARAM) as BcStatusParam[]

/**
 * `?ref=` on the list page: the rejection email's link (FF-2855) names the
 * transaction by its reference; the page opens it.
 */
export const BC_REF_PARAM = 'ref'

/** `?view=` on the list page. */
export const BC_VIEW = {
    list: 'list',
    grouped: 'grouped',
} as const
export type BcView = (typeof BC_VIEW)[keyof typeof BC_VIEW]

/** Route paths, relative to the settings module root. */
export const BC_ROUTE_LIST = '/admin/transactions-to-review'
export const bcRouteTransaction = (id: number) => `${BC_ROUTE_LIST}/${id}`

/** Limits Business Central enforces (the server refuses past them). */
export const BC_LIMITS = {
    /** Description field, characters. */
    DESCRIPTION_MAX: 100,
    /** Vendor and account numbers, characters. Upper-cased by the server. */
    CODE_MAX: 20,
    /** Amounts: at most two decimals, written with a full stop. */
    AMOUNT_DECIMALS: 2,
    /** Vendor search page size (API default 25, max 50). */
    VENDOR_TOP: 25,
} as const

/** After a Queued resend: poll the transaction this often… */
export const BC_POLL_INTERVAL_MS = 3_000
/** …and stop after this long, saying it is still waiting. */
export const BC_POLL_TIMEOUT_MS = 60_000
/** Vendor search typing debounce. */
export const BC_VENDOR_SEARCH_DEBOUNCE_MS = 300
